import { Room } from 'colyseus.js';

/**
 * Shared input-send helpers for the movement/combat wire protocol
 * (design/gdd/movement-netcode.md, design/gdd/melee-combat.md), used by
 * dungeon-instance-bootstrap.ts. Separate from the template's Socket.io
 * client (core/network/socket.ts) — Colyseus runs its own WebSocket
 * transport per movement-netcode.md's decision to keep the real-time
 * combat layer independent of BaseGateway.
 */

export type SwingDirection = 'LEFT' | 'RIGHT' | 'OVERHEAD';

export function sendMove(room: Room, direction: { x: number; z: number }): void {
  room.send('input.move', { direction, clientTs: performance.now() });
}

/**
 * camera-and-controls.md amendment (2026-08-25, fifth) — where this player is
 * looking. Its own message rather than a field on `input.move`, because a
 * player standing still and turning to face something has to be able to tell
 * the server they turned: `input.move` is only sent when the movement
 * direction changes, and turning on the spot changes nothing about it.
 */
export function sendAim(room: Room, yaw: number): void {
  room.send('input.aim', { yaw, clientTs: performance.now() });
}

export function sendSprint(room: Room, active: boolean): void {
  room.send('input.sprint', { active, clientTs: performance.now() });
}

/** camera-and-controls.md amendment (2026-08-20). */
export function sendCrouch(room: Room, active: boolean): void {
  room.send('input.crouch', { active, clientTs: performance.now() });
}

/** Jump (2026-10-16) — replaced `sendDodge` when dodge became passive. Space / the touch Jump plate. */
export function sendJump(room: Room): void {
  room.send('input.jump', { clientTs: performance.now() });
}

export function sendAttack(room: Room, swingDirection: SwingDirection): void {
  room.send('input.attack', { weaponSlot: 0, swingDirection, clientTs: performance.now() });
}

export function sendParry(room: Room): void {
  room.send('input.parry', { clientTs: performance.now() });
}

/** combat-depth.md (2026-08-21) — cancels an in-progress attack windup. */
export function sendFeint(room: Room): void {
  room.send('input.feint', { clientTs: performance.now() });
}

/** combat-depth.md (2026-08-21) — hold-to-block. */
export function sendBlock(room: Room, active: boolean): void {
  room.send('input.block', { active, clientTs: performance.now() });
}

/** design/gdd/class-skills-abilities.md — activates the player's one, class-determined ability. */
export function sendAbility(room: Room): void {
  room.send('input.ability', { clientTs: performance.now() });
}

/** design/gdd/lighting.md (2026-08-21) — toggles the sending player's personal lantern. */
export function sendLanternToggle(room: Room): void {
  room.send('input.lantern_toggle', { clientTs: performance.now() });
}

/** design/gdd/cosmetics-store.md — plays an owned premium emote. Ownership is re-checked server-side; this only asks. */
export function sendEmote(room: Room, emoteId: string): void {
  room.send('input.emote', { emoteId });
}
